// Platform detection for the install/notification flow.
//
// Two Apple rules drive everything here:
//  1. On iPhone, notifications only work after the page has been added to the
//     Home Screen and opened from that icon.
//  2. Only Safari can create that icon. Other iPhone browsers (Chrome, Firefox,
//     Edge) can make a shortcut, but not the installed app that can notify you.
// So the UI has to know which browser someone is in, not just which phone.

export function isIOS(): boolean {
  const ua = window.navigator.userAgent;
  // iPadOS 13+ reports as "Macintosh"; touch points disambiguate it.
  const iPadOS = /Macintosh/i.test(ua) && (navigator as any).maxTouchPoints > 1;
  return /iphone|ipad|ipod/i.test(ua) || iPadOS;
}

// Chrome/Firefox/Edge/Opera on iOS. They all run on WebKit under the hood, but
// none of them can produce a Home Screen app that receives notifications — so
// these users have to be sent to Safari before any of the steps make sense.
export function isIOSOtherBrowser(): boolean {
  return isIOS() && /CriOS|FxiOS|EdgiOS|OPiOS/i.test(window.navigator.userAgent);
}

// True once the app is running from the Home Screen icon (iOS) or as an
// installed app (Android/desktop).
export function isStandalone(): boolean {
  return (
    (window.navigator as any).standalone === true ||
    window.matchMedia?.("(display-mode: standalone)").matches === true
  );
}

// What the member needs to do next before notifications can work.
export type PushStep = "wrong_browser" | "install_first" | "can_enable" | "unsupported";

export function pushStep(): PushStep {
  if (isStandalone()) {
    return "Notification" in window ? "can_enable" : "unsupported";
  }
  if (isIOSOtherBrowser()) return "wrong_browser";
  if (isIOS()) return "install_first";
  if (!("Notification" in window)) return "unsupported";
  return "can_enable";
}
